import { unwrap } from "@majourneys/api-client";
import { api } from "../../../lib/api";
import type { BucketItemSnapshot, BucketAddInput } from "../state/bucket-types";

interface ServerBucketItem {
  id: string;
  activityId: string;
  activitySlug: string;
  activityTitle: string;
  cityName: string;
  image: string | null;
  typePrice: 0 | 1 | 2 | 3;
  packageId: string;
  packageName: string;
  maxGuests?: number | null;
  includedGuests?: number | null;
  pricing: {
    adult: number;
    child: number;
    infant: number;
    transport: number;
    transportLarge?: number | null;
    transportOutside?: number | null;
    extraGuest?: number | null;
  };
  date: string;
  dateEnd: string | null;
  time: string | null;
  adults: number;
  children: number;
  infants: number;
  hours: number | null;
  nights: number | null;
  groups: number | null;
  transport: boolean;
  transportOutside: boolean;
  priceSnapshot: number;
  notes: string | null;
  addedAt: string | null;
  updatedAt: string | null;
}

function fromServer(it: ServerBucketItem): BucketItemSnapshot {
  return {
    id: it.id,
    activityId: it.activityId,
    activitySlug: it.activitySlug,
    packageId: it.packageId,
    date: it.date,
    dateEnd: it.dateEnd,
    time: it.time,
    adults: it.adults,
    children: it.children,
    infants: it.infants,
    hours: it.hours,
    nights: it.nights,
    groups: it.groups,
    transport: it.transport,
    transportOutside: it.transportOutside ?? false,
    notes: it.notes,
    thumbnail: it.image,
    title: it.activityTitle,
    cityName: it.cityName,
    typePrice: it.typePrice,
    maxGuests: it.maxGuests ?? null,
    includedGuests: it.includedGuests ?? null,
    pricing: it.pricing,
    priceSnapshot: it.priceSnapshot,
    addedAt: it.addedAt ?? new Date().toISOString(),
  };
}

function toServerLine(input: BucketAddInput) {
  return {
    activitySlug: input.activitySlug,
    packageId: input.packageId,
    date: input.date,
    dateEnd: input.dateEnd ?? null,
    time: input.time ?? null,
    adults: input.adults,
    children: input.children,
    infants: input.infants,
    hours: input.hours ?? null,
    groups: input.groups ?? null,
    transport: input.transport,
    transportOutside: input.transportOutside ?? false,
    notes: input.notes ?? null,
  };
}

export const bucketApi = {
  list: async (): Promise<BucketItemSnapshot[]> => {
    const body = unwrap(await api.bucket.get()) as { items: ServerBucketItem[] };
    return body.items.map(fromServer);
  },
  add: async (input: BucketAddInput): Promise<BucketItemSnapshot | null> => {
    const body = unwrap(await api.bucket.post(toServerLine(input))) as {
      item: ServerBucketItem | null;
      count: number;
    };
    return body.item ? fromServer(body.item) : null;
  },
  update: async (
    id: string,
    patch: Partial<Omit<BucketAddInput, "activityId" | "activitySlug">>,
  ): Promise<BucketItemSnapshot | null> => {
    const body = unwrap(
      await api.bucket({ id }).patch({
        packageId: patch.packageId,
        date: patch.date,
        dateEnd: patch.dateEnd ?? undefined,
        time: patch.time ?? undefined,
        adults: patch.adults,
        children: patch.children,
        infants: patch.infants,
        hours: patch.hours ?? undefined,
        groups: patch.groups ?? undefined,
        transport: patch.transport,
        transportOutside: patch.transportOutside,
        notes: patch.notes ?? undefined,
      }),
    ) as { item: ServerBucketItem | null; count: number };
    return body.item ? fromServer(body.item) : null;
  },
  remove: async (id: string): Promise<void> => {
    unwrap(await api.bucket({ id }).delete());
  },
  sync: async (items: BucketAddInput[]): Promise<BucketItemSnapshot[]> => {
    const body = unwrap(
      await api.bucket.sync.post({ items: items.map(toServerLine) }),
    ) as { items: ServerBucketItem[] };
    return body.items.map(fromServer);
  },
};
